import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, getIdTokenResult } from 'firebase/auth';
import { useIntl } from 'react-intl';
import { PageTitle, useLayout } from '../../../_metronic/layout/core';
import { UserDashboard } from './UserDashboard';
import { AdminDashboard } from './AdminDashboard';
import { useTerminalTheme } from '../../hooks/useTerminalTheme';
import { useGraphiteTheme } from '../../hooks/useGraphiteTheme';
import HomePage from '../home/HomePage';
import { GraphiteHomePage } from '../../themes/graphite';

const DashboardPage = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const isTerminal = useTerminalTheme();
  const isGraphite = useGraphiteTheme();

  useEffect(() => {
    const auth = getAuth();
    const unregisterAuthObserver = onAuthStateChanged(auth, async (user) => {
      if(user) {
        const token = await getIdTokenResult(user);
        if (typeof token.claims.isAdmin === 'boolean') {
          setIsAdmin(token.claims.isAdmin);
        } else {
          // Handle the error, `token.claims.isAdmin` is not a boolean
        }
      } else {
        setIsAdmin(false);
      }
    });
    return () => unregisterAuthObserver(); // Make sure we un-register Firebase observers when the component unmounts.
  }, []);

  // Terminal + Graphite themes each opt into their own bespoke dashboard.
  // The classic user/admin dashboards are unchanged — they render on any
  // other theme (e.g. Metronic light/dark).
  if (isTerminal) {
    return <HomePage />;
  }
  if (isGraphite) {
    return <GraphiteHomePage />;
  }

  return isAdmin ? <AdminDashboard /> : <UserDashboard />;
};

const DashboardWrapper = () => {
  const intl = useIntl();
  const { config, setLayout } = useLayout();

  // Make dashboard (app "home") use full-width content container (matches Settings wide layout).
  useEffect(() => {
    const prevWidth = config?.content?.width;
    if (prevWidth !== 'fluid') {
      setLayout({ content: { width: 'fluid' } } as any);
    }
    return () => {
      if (prevWidth && prevWidth !== 'fluid') {
        setLayout({ content: { width: prevWidth } } as any);
      }
    };
    // Intentionally run once; we snapshot prevWidth for correct restore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <PageTitle breadcrumbs={[]}>
        {intl.formatMessage({ id: 'MENU.DASHBOARD' })}
      </PageTitle>
      <DashboardPage />
    </>
  );
};

export { DashboardWrapper };
